import { Component, ViewChild, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RuleListComponent } from '../../components/rule-list/rule-list.component';
import { RuleFormComponent } from '../../components/rule-form/rule-form.component';
import { RuleService } from '../../services/rule.service';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { CatalogService } from '@services/data/catalog.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import Swal from 'sweetalert2';
import { NgbModal, NgbModalRef, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-management',
    standalone: true,
    imports: [
        CommonModule,
        RuleListComponent,
        RuleFormComponent,
        NgbModalModule,
        PageHeaderComponent,
        MobileFooterComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './rule-management.component.html'
})
export class RuleManagementComponent {

    @ViewChild(RuleListComponent) ruleList!: RuleListComponent;
    @ViewChild('ruleModal') ruleModal: any;
    private modalRef: NgbModalRef;
    selectedRule: any = null;
    ruleForm: FormGroup;
    mode = signal<FormMode>(FormMode.VIEW);
    formMode = FormMode;
    showDetail = false;

    private fb = inject(FormBuilder);
    private ruleService = inject(RuleService);
    private catalogService = inject(CatalogService);
    private modalService = inject(NgbModal);
    readonly perm = inject(PermissionService);
    public appSettings = inject(AppSettings);

    public Toast = Swal.mixin({
        toast: true, 
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
    });
    constructor() {
        this.ruleForm = this.fb.group({
            id: [null],
            code: [null, [Validators.required]],
            description: [null, [Validators.required]],
            minRange: [null],
            maxRange: [null],
            typeValue: [null],
            value: [null, [Validators.required]]
        });
    }

    get showMobileDetail(): boolean {
        return this.appSettings.isMobile() && this.showDetail;
    }

    onRuleSelected(rule: any) {
        this.openEditor(rule, FormMode.EDIT);
    }

    createNewRule() {
        this.selectedRule = { isNew: true }; // Marker for UI to show form but not Model list yet
        this.mode.set(FormMode.CREATE);
        this.ruleForm.reset();
    }

    handleFormChange(formValue: any) {
        // If null, it means deactivation/delete
        if (formValue === null) {
            this.selectedRule = null;
            this.ruleList.loadRules();
            return;
        }

        const data = formValue;

        if (this.mode() === FormMode.EDIT) {
            if (!this.selectedRule?.id) return;
            this.updateRule(data);

        } else if (this.mode() === FormMode.CREATE) {
            this.createRule(data);

        }
    }


    updateRule(data: any) {
            const payload = this._rulePayload(data);
            this.ruleService.update(this.selectedRule.id, payload).subscribe({
                next: () => {
                    this.Toast.fire({ icon: 'success', title: 'Regla actualizada correctamente' });
                    this.ruleList.loadRules();
                    this.catalogService.reloadRules();
                    this.selectedRule = { ...this.selectedRule, ...payload, code: payload.slug };
                    this.mode.set(FormMode.VIEW);
                    this.ruleForm.patchValue({ ...payload, code: payload.slug });
                    this.closeDetail();
                },
                error: () => this.Toast.fire({ icon: 'error', title: 'No se pudo actualizar la regla' })
            });
    }

    createRule(data: any) {
                const payload = this._rulePayload(data);
                this.ruleService.create(payload).subscribe({
                next: (res) => {
                    this.Toast.fire({ icon: 'success', title: 'Regla creada correctamente' });
                    this.ruleList.loadRules();
                    this.catalogService.reloadRules();
                    this.selectedRule = res;
                    this.mode.set(FormMode.VIEW);
                    this.ruleForm.patchValue({ ...this.selectedRule, code: this.selectedRule?.slug || this.selectedRule?.code });
                    this.closeDetail();
                },
                error: () => this.Toast.fire({ icon: 'error', title: 'No se pudo crear la regla' })
            });
    }

    private _rulePayload(data: any) {
        const payload = { ...data };
        payload.slug = String(payload.code || payload.slug || '').trim().toUpperCase();
        delete payload.code;
        delete payload.category;
        return payload;
    }

    saveRule(data: any) {
        this.handleFormChange(data);
    }

    cancelRule() {
        this.mode.set(FormMode.VIEW);
        this.ruleForm.reset();
    }

  open(content, size: 'md' | 'lg' | 'xl' = 'lg') {
    this.modalRef = this.modalService.open(content, { size: size });
  }

  close() {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  closeDetail() {
    this.showDetail = false;
    this.selectedRule = null;
    this.mode.set(FormMode.VIEW);
    this.close();
  }

    newRuleModal() {
        this.openEditor(null, FormMode.CREATE);
    }

    private openEditor(rule: any | null, mode: FormMode) {
        this.selectedRule = rule;
        this.mode.set(mode);
        this.ruleForm.reset();
        if (rule) {
            this.ruleForm.patchValue({
                ...rule,
                code: rule.slug || rule.code
            });
        }
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        if (this.appSettings.isMobile()) {
            this.showDetail = true;
            return;
        }
        this.open(this.ruleModal);
    }
}
